import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Send, Mail, Loader2, AlertTriangle, CheckCircle2, XCircle } from "lucide-react";
import { api } from "../lib/api";
import { formatBytes, formatDate } from "../lib/format";
import Tip from "../components/Tip";
import AccountantSendModal from "../components/AccountantSendModal";

/**
 * Task 3 · Fatture Commercialista.
 *
 * Piera flags documents as "to send to accountant" from the DocumentDetail
 * page; those documents land here grouped by fiscal_year. From here she
 * selects a subset (or all), previews the recipient, and sends via Brevo
 * with an L2 password confirmation.
 */
const YEARS = (() => {
  const y = new Date().getFullYear();
  return [y + 1, y, y - 1, y - 2, y - 3];
})();

export default function AccountantInbox() {
  const [year, setYear] = useState(null); // null = "tutti gli anni"
  const [inbox, setInbox] = useState(null);
  const [history, setHistory] = useState([]);
  const [settings, setSettings] = useState({ accountant_email: "", accountant_name: "" });
  const [savingSettings, setSavingSettings] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    setLoading(true);
    try {
      const [i, h, s] = await Promise.all([
        api.accountantInbox(year),
        api.accountantHistory(20),
        api.accountantGetSettings(),
      ]);
      setInbox(i); setHistory(h); setSettings(s);
    } catch (e) {
      toast.error("Errore caricamento");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [year]);

  const toggleAll = () => {
    if (!inbox) return;
    if (selectedIds.size === inbox.documents.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(inbox.documents.map((d) => d.id)));
  };
  const toggleOne = (id) => {
    const next = new Set(selectedIds);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelectedIds(next);
  };

  const selectedDocs = (inbox?.documents || []).filter((d) => selectedIds.has(d.id));
  const selectedSize = selectedDocs.reduce((s, d) => s + (d.size_bytes || 0), 0);
  const MAX_BATCH_BYTES = 8 * 1024 * 1024;
  const overSize = selectedSize > MAX_BATCH_BYTES;

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const patched = await api.accountantPatchSettings({
        accountant_email: settings.accountant_email || null,
        accountant_name: settings.accountant_name || null,
      });
      setSettings(patched);
      toast.success("Contatto commercialista aggiornato");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore salvataggio");
    } finally {
      setSavingSettings(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="accountant-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-semibold" data-testid="accountant-title">Fatture Commercialista</h1>
          <p className="text-sm opacity-70 mt-1">
            Documenti fiscali contrassegnati da inviare al commercialista.
            L'invio richiede conferma password.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs opacity-70">Anno fiscale</label>
          <select
            value={year || ""}
            onChange={(e) => setYear(e.target.value ? Number(e.target.value) : null)}
            className="rounded-lg border px-2 py-1.5 text-sm min-h-[36px]"
            data-testid="accountant-year-filter"
          >
            <option value="">Tutti</option>
            {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>

      {/* Contatto commercialista */}
      <div className="pxos-card p-4 space-y-3" data-testid="accountant-contact">
        <div className="flex items-center gap-2 text-sm font-semibold">
          <Mail size={16} /> Contatto commercialista
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <input
            type="text"
            value={settings.accountant_name || ""}
            onChange={(e) => setSettings((s) => ({ ...s, accountant_name: e.target.value }))}
            placeholder="Nome (es. Studio Commercialista Rossi)"
            className="rounded-lg border px-3 py-2 text-sm min-h-[40px]"
            data-testid="accountant-name-input"
          />
          <input
            type="email"
            value={settings.accountant_email || ""}
            onChange={(e) => setSettings((s) => ({ ...s, accountant_email: e.target.value }))}
            placeholder="Email (es. mario.rossi@studio.it)"
            className="rounded-lg border px-3 py-2 text-sm min-h-[40px]"
            data-testid="accountant-email-input"
          />
        </div>
        <div className="flex justify-end">
          <button
            onClick={saveSettings}
            disabled={savingSettings}
            className="pxos-btn-primary text-xs px-4 py-2 min-h-[36px] disabled:opacity-50"
            data-testid="accountant-save-settings"
          >
            {savingSettings ? "Salvo…" : "Salva"}
          </button>
        </div>
      </div>

      {/* Documenti da inviare */}
      <div className="pxos-card p-4 space-y-3" data-testid="accountant-inbox">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="text-sm">
            <strong data-testid="accountant-inbox-count">{inbox?.total || 0}</strong> documenti pronti · {formatBytes(inbox?.total_size_bytes || 0)}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={toggleAll}
              className="text-xs px-3 py-1.5 border rounded-lg hover:bg-black/5 min-h-[36px]"
              data-testid="accountant-toggle-all"
            >
              {inbox && selectedIds.size === inbox.documents.length ? "Deseleziona tutti" : "Seleziona tutti"}
            </button>
            <Tip label={
              !settings.accountant_email ? "Configura prima l'email del commercialista"
              : selectedIds.size === 0 ? "Seleziona almeno un documento"
              : overSize ? "Batch troppo grande (>8 MB)"
              : `Invia ${selectedIds.size} documenti al commercialista`
            }>
              <span>
                <button
                  onClick={() => setModalOpen(true)}
                  disabled={selectedIds.size === 0 || !settings.accountant_email || overSize}
                  className="pxos-btn-primary text-xs px-4 py-2 min-h-[36px] flex items-center gap-1.5 disabled:opacity-40"
                  data-testid="accountant-send-btn"
                >
                  <Send size={14} /> Invia al commercialista ({selectedIds.size})
                </button>
              </span>
            </Tip>
          </div>
        </div>
        {overSize && (
          <div className="text-xs text-red-600 flex items-center gap-1" data-testid="accountant-oversize-warning">
            <AlertTriangle size={14} /> Dimensione selezione ({formatBytes(selectedSize)}) supera il limite di 8 MB.
          </div>
        )}
        {loading ? (
          <div className="opacity-60 text-sm py-6 text-center"><Loader2 className="inline animate-spin" size={16} /> Carico…</div>
        ) : !inbox || inbox.total === 0 ? (
          <div className="opacity-60 text-sm py-8 text-center" data-testid="accountant-empty">
            Nessun documento da inviare{year ? ` per il ${year}` : ""}. Contrassegna un documento come "Da inviare al commercialista" dalla sua pagina di dettaglio.
          </div>
        ) : (
          <div className="divide-y">
            {inbox.documents.map((d) => (
              <label key={d.id} className="flex items-center gap-3 py-2.5 cursor-pointer" data-testid={`accountant-doc-${d.id}`}>
                <input
                  type="checkbox"
                  checked={selectedIds.has(d.id)}
                  onChange={() => toggleOne(d.id)}
                  className="w-4 h-4"
                  data-testid={`accountant-doc-toggle-${d.id}`}
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{d.title || d.filename}</div>
                  <div className="text-xs opacity-60 flex items-center gap-2 flex-wrap">
                    {d.category && <span className="uppercase">{d.category}</span>}
                    {d.provider_name && <span>· {d.provider_name}</span>}
                    {d.amount != null && <span>· € {d.amount}</span>}
                    {d.document_date && <span>· {formatDate(d.document_date)}</span>}
                    {d.fiscal_year && <span>· FY {d.fiscal_year}</span>}
                    <span>· {formatBytes(d.size_bytes)}</span>
                  </div>
                </div>
              </label>
            ))}
          </div>
        )}
      </div>

      {/* Storico invii */}
      <div className="pxos-card p-4 space-y-2" data-testid="accountant-history">
        <div className="text-sm font-semibold mb-1">Storico invii</div>
        {history.length === 0 ? (
          <div className="opacity-60 text-xs py-3">Nessun invio precedente.</div>
        ) : (
          <div className="divide-y text-xs">
            {history.map((b) => (
              <div key={b.id} className="py-2 flex items-center justify-between gap-3" data-testid={`accountant-history-${b.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    {b.status === "sent" ? (
                      <CheckCircle2 size={14} className="text-green-600" />
                    ) : (
                      <XCircle size={14} className="text-red-600" />
                    )}
                    <span className="font-medium truncate">{b.subject}</span>
                  </div>
                  <div className="opacity-60 mt-0.5">
                    {formatDate(b.sent_at)} · {b.recipient_email} · {b.document_count} doc · {formatBytes(b.total_size_bytes)}
                    {b.fiscal_year ? ` · FY ${b.fiscal_year}` : ""}
                  </div>
                  {b.status === "failed" && b.error_message && (
                    <div className="text-red-600 mt-0.5 truncate">{b.error_message}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <AccountantSendModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        docs={selectedDocs}
        totalSize={selectedSize}
        fiscalYear={year}
        recipientEmail={settings.accountant_email}
        recipientName={settings.accountant_name}
        onSuccess={() => { setSelectedIds(new Set()); reload(); }}
      />
    </div>
  );
}
